import {
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
  styled,
  CircularProgress,
} from '@mui/material';
import { buttonClasses } from '@mui/material/Button';
import { vars } from '@tgtg/kit';
import { forwardRef, type MouseEventHandler } from 'react';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  iconButton?: boolean;
  variant?: MuiButtonProps['variant'] | 'ghost' | 'danger' | 'text' | 'subtle' | 'accent';
  isPending?: boolean;
}

const StyledCircularProgress = styled(CircularProgress, {
  shouldForwardProp: (prop) => prop !== 'iconButton',
})<{ iconButton: boolean }>(({ theme: { spacing, palette }, ...props }) => ({
  marginLeft: props.iconButton ? spacing(0) : spacing(2),
  color: palette.misc.progressSpinner.primary,
  position: 'absolute',
  margin: 'auto',
}));

const StyledMuiVariantButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'isPending' && prop !== 'iconButton' && prop !== 'compact',
})<ButtonProps>(({ theme: { spacing, palette } }) => ({
  padding: spacing(1, 2),
  borderRadius: '24px',
  minHeight: '40px',
  boxShadow: 'none',
  minWidth: 'auto',
  textTransform: 'none',
  fontWeight: '500',
  position: 'relative',
  [`&.${buttonClasses.sizeSmall}`]: {
    minHeight: '32px',
  },
  [`&.${buttonClasses.contained}`]: {
    backgroundColor: palette.button.primary,
    '&:hover': {
      backgroundColor: palette.button.primaryHover,
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: palette.button.primaryActive,
      boxShadow: 'none',
    },
    [`&.${buttonClasses.disabled}`]: {
      backgroundColor: palette.button.disabled,
      color: palette.text.disabled,
    },
  },
  [`&.${buttonClasses.containedSecondary}`]: {
    backgroundColor: palette.button.secondaryPrimary,
    color: palette.text.secondary,
    '&:hover': {
      backgroundColor: palette.button.secondaryPrimary,
      boxShadow: 'none',
      color: palette.cvi.primary[20],
    },
    '&:active': {
      backgroundColor: palette.button.secondaryPrimary,
      boxShadow: 'none',
      opacity: 0.8,
    },
    [`&.${buttonClasses.disabled}`]: {
      backgroundColor: palette.button.disabled,
      color: palette.text.disabled,
    },
  },
  [`&.${buttonClasses.outlined}`]: {
    boxShadow: `inset 0px 0px 0px 2px ${palette.button.primary}`,
    color: palette.button.primary,
    border: 'none',
    '&:hover': {
      boxShadow: `inset 0px 0px 0px 2px ${palette.button.primaryHover}`,
      color: palette.button.primaryHover,
      backgroundColor: 'transparent',
    },
    '&:active': {
      boxShadow: `inset 0px 0px 0px 2px ${palette.button.primaryActive}`,
      color: palette.button.primaryActive,
    },
    [`&.${buttonClasses.disabled}`]: {
      boxShadow: `inset 0px 0px 0px 2px ${palette.border.disabled}`,
      color: palette.text.disabled,
      backgroundColor: palette.button.disabled,
    },
  },
  [`&.${buttonClasses.outlinedSecondary}`]: {
    boxShadow: `inset 0px 0px 0px 2px ${palette.button.secondaryPrimary}`,
    color: palette.button.secondaryPrimary,
    border: 'none',
    '&:hover': {
      boxShadow: `inset 0px 0px 0px 2px ${palette.button.secondaryPrimary}`,
      color: palette.button.secondaryPrimary,
    },
    '&:active': {
      boxShadow: `inset 0px 0px 0px 2px ${palette.button.secondaryPrimary}`,
      color: palette.button.secondaryPrimary,
      opacity: 0.8,
    },
    [`&.${buttonClasses.disabled}`]: {
      boxShadow: `inset 0px 0px 0px 2px ${palette.border.disabled}`,
      color: palette.text.disabled,
      backgroundColor: palette.button.disabled,
      opacity: 0.72,
    },
  },
  [`&.${buttonClasses.text}`]: {
    padding: 0,
    color: palette.button.primary,
    '&:hover': {
      color: palette.button.primaryHover,
      backgroundColor: 'transparent',
    },
    '&:active': {
      color: palette.button.primaryActive,
    },
    [`&.${buttonClasses.disabled}`]: {
      color: palette.text.disabled,
    },
    [`&.${buttonClasses.focusVisible}`]: {
      padding: '6px 14px',
      borderRadius: spacing(3),
      border: `2px solid ${palette.cvi.primary[30]}`,
      outline: 'none',
    },
    [`&.${buttonClasses.textError}`]: {
      color: palette.text.error,
      '&:hover': {
        color: palette.cvi.red[30],
      },
      '&:active': {
        color: palette.cvi.red[50],
      },
    },
  },
  //Danger
  '&.MuiButton-dangerPrimary': {
    backgroundColor: palette.button.dangerPrimary,
    color: palette.cvi.neutrals[10],
    '&:hover': {
      backgroundColor: palette.button.dangerHover,
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: palette.button.dangerActive,
      boxShadow: 'none',
    },
    [`&.${buttonClasses.disabled}`]: {
      backgroundColor: palette.button.disabled,
      color: palette.text.disabled,
    },
  },
  //ghost
  [`&.MuiButton-ghostPrimary`]: {
    padding: spacing(1, 2),
    color: palette.button.ghostPrimary,
    backgroundColor: 'transparent',
    '&:hover': {
      color: palette.button.ghostHover,
      backgroundColor: 'transparent',
      textDecorationLine: 'underline',
      textDecorationThickness: '1px',
    },
    '&:active': {
      color: palette.button.ghostActive,
    },
    [`&.${buttonClasses.disabled}`]: {
      color: palette.text.disabled,
    },
    [`&.${buttonClasses.focusVisible}`]: {
      padding: '6px 14px',
      borderRadius: '24px',
      border: `2px solid ${palette.cvi.primary[30]}`,
    },
  },
  // Subtle
  ['&.MuiButton-subtlePrimary']: {
    backgroundColor: vars.color.surface.interactive.subtle,
    color: vars.color.content.primary,
    '&:hover': {
      backgroundColor: vars.color.surface.interactive.subtleHover,
    },
  },
  // Accent
  ['&.MuiButton-accentPrimary']: {
    backgroundColor: vars.color.surface.interactive.accent,
    color: vars.color.content.onAccent,
    '&:hover': {
      backgroundColor: vars.color.surface.interactive.accentHover,
    },
  },
}));

type CastedForwardRefButtonType = <C extends React.ElementType>(
  props: MuiButtonProps<C, { component?: C; onClick?: MouseEventHandler<HTMLButtonElement> }>,
  ref?: React.Ref<HTMLButtonElement>,
) => React.ReactElement;

const ButtonVariants = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'contained', ...props }, ref) => {
    return <StyledMuiVariantButton variant={variant} {...props} ref={ref} />;
  },
);

const CastedForwardRefButtonFnc: CastedForwardRefButtonType = (props, ref) => {
  return (
    <>
      <ButtonVariants disableRipple ref={ref} {...props}>
        {props.iconButton ? (
          <>
            {props.isPending ? (
              <StyledCircularProgress iconButton={true} size={24} />
            ) : (
              props.children
            )}
          </>
        ) : (
          <>
            {props.children}
            {props.isPending && <StyledCircularProgress iconButton={false} size={24} />}
          </>
        )}
      </ButtonVariants>
    </>
  );
};

const Button = forwardRef(CastedForwardRefButtonFnc) as CastedForwardRefButtonType;

export default Button;
